import { onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import throttle from 'lodash/throttle';

import { lastActivityStorage } from '@/storages/browser.storage';
import { postSession } from '@/utils/session-channel';
import { ACTIVITY_EVENTS, ACTIVITY_THROTTLE_MS } from '@/app-constants/session.constant';

/**
 * Records real user activity as a last-activity timestamp.
 *
 * Per requirement: the listeners ONLY store a timestamp — they never arm a timer
 * or trigger a session action. The decision (warn / logout) lives entirely in
 * use-inactivity-monitor, which polls this timestamp. Writing to localStorage
 * makes the idle clock cross-tab for free; the BroadcastChannel post adds
 * cross-tab immediacy.
 */
export const useActivityTracker = (): void => {
  const router = useRouter();
  let removeNavGuard: (() => void) | undefined;

  const recordActivity = throttle(
    (): void => {
      const ts = Date.now();
      lastActivityStorage.set(ts); // the only durable side effect
      postSession({ type: 'activity', ts }); // cross-tab immediacy (req 6)
    },
    ACTIVITY_THROTTLE_MS,
    { leading: true, trailing: true },
  );

  // Stable wrapper with a precise listener type, so add/removeEventListener share
  // one reference and the throttled fn's lodash typing doesn't leak to the DOM API.
  const handleActivity = (): void => {
    recordActivity();
  };

  onMounted(() => {
    lastActivityStorage.set(Date.now()); // seed so a fresh load isn't treated as idle
    ACTIVITY_EVENTS.forEach((eventName) => window.addEventListener(eventName, handleActivity, { passive: true }));
    removeNavGuard = router.afterEach(() => recordActivity()); // page navigation counts as activity
  });

  onUnmounted(() => {
    ACTIVITY_EVENTS.forEach((eventName) => window.removeEventListener(eventName, handleActivity));
    removeNavGuard?.();
    recordActivity.cancel();
  });
};
